import { useCartStore } from "@/store/CartStore";
import { useNavigate } from "@tanstack/react-router";
import { checkoutRoute, locationRoute, menuRoute } from "@/App";
import { allMenusQueryOptions, MenuItem } from "@/hooks/useMenu";
import { useSuspenseQuery } from "@tanstack/react-query";
import QuantityInput from "@/components/QuantityInput";
import { useContext, useEffect, useMemo } from "react";
import { useTables } from "@/hooks/useTables";
import { cn, formatPriceToDkk, getImageUrl } from "@/lib/utils";
import { ImageMinus, AlertTriangle } from "lucide-react";
import { useTrackingEvent } from "@/hooks/useTrackingEvent";
import { MenuContext } from "@/routes/Menu";
import { LocationConfigurationContext } from "@/providers/LocationConfigurationProvider";
import { Button } from "./ui/button";
import { BusyDialogs } from "./BusyDialogs";
import { ModeIndicator } from "./ModeIndicator";
import { useUpsales } from "@/hooks/useUpsales";
import { useRebate } from "@/hooks/useRebate";

export default function Cart({
  setCartIsOpen,
  setShowUpsales,
}: {
  setCartIsOpen?: (open: boolean) => void;
  setShowUpsales?: (open: boolean) => void;
}) {
  const { showTableSelector, lockTableSelector, upsales, rebate } = useContext(
    LocationConfigurationContext
  );

  const { handleEditCartItem } = useContext(MenuContext);
  const {
    cart,
    setCount,
    price,
    table,
    setTable,
    removedItems,
    clearRemovedItems,
  } = useCartStore();
  const { locationId } = locationRoute.useParams();
  const { menuId } = menuRoute.useParams();
  const menuQuery = useSuspenseQuery(allMenusQueryOptions(locationId));
  const menus = menuQuery.data;
  const navigate = useNavigate();
  const { data: tables } = useTables(locationId, true);
  const tracking = useTrackingEvent();
  // const { data: isChequeOpen } = useTableOpen(menuId, table);
  const isChequeOpen = false;

  const { hasUpsale } = useUpsales(cart, menuQuery.data, upsales);
  const { isEligible: hasRebate, discountAmount, finalPrice, setsCount, qualifyingCount } = useRebate(cart, menus, rebate, price);

  const flattenedMenus = useMemo(() => {
    if (menus) {
      return menus
        .map((menu) =>
          Object.values(menu.categories).reduce(
            (acc, category) => [...acc, ...category.products],
            [] as MenuItem[]
          )
        )
        .flat();
    }

    return [];
  }, [menus]);

  useEffect(() => {
    if (showTableSelector) return;
    if (table !== undefined) return;

    // If not showing table selector, and table isn't already selected via QR - select first table
    if (!tables || tables.length === 0) return;
    setTable(tables[0].id);
  }, [showTableSelector, tables, table]);

  const productCount = cart.reduce((acc, item) => acc + item.count, 0);

  const cartIsEmpty = cart.length === 0 || !menus;

  function goToCheckout() {
    try {
      tracking.capture("checkout_clicked", {
        locationId,
        value: price,
      });
    } catch (error) {
      console.error("Error capturing checkout clicked event:", error);
    }

    navigate({
      to: checkoutRoute.to,
      params: { menuId, locationId },
      search: {
        table: table,
      },
    });
  }

  return (
    <>
      <div className="relative bg-background text-foreground w-full rounded-t-xl md:rounded-xl h-full flex flex-col overflow-hidden">
        {/* Busy Dialogs */}
        <div className="empty:hidden rounded-t-xl overflow-hidden">
          <BusyDialogs />
        </div>

        {/* Fixed Header */}
        <header className="py-4 px-4 border-b flex-shrink-0">
          <div className="flex flex-col">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-medium tracking-tight text-foreground sm:text-4xl mb-0">
                {cartIsEmpty ? "Your cart is empty" : "Your order"}
              </h1>

              {showTableSelector && cartIsEmpty === false && (
                <div className="md:mr-14">
                  <ModeIndicator disabled={lockTableSelector ?? false} />
                </div>
              )}
            </div>

            <h2 className="font-medium tracking-tight text-muted-foreground">
              {productCount} {productCount === 1 ? "item" : "items"}
            </h2>
          </div>
        </header>

        {cartIsEmpty ? (
          /* Empty Cart Content */
          <div className="flex flex-col items-center justify-center flex-1 gap-4 px-4">
            <p className="text-sm text-muted-foreground">
              Add items to your order to get started.
            </p>

            <Button
              type="button"
              variant={"default"}
              onClick={() => {
                navigate({
                  to: menuRoute.to,
                  params: { menuId },
                  search: { table },
                });
                if (setCartIsOpen) setCartIsOpen(false);
              }}
            >
              Go back to menu
            </Button>
          </div>
        ) : (
          <>
            {/* Scrollable Cart Items */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <ul
                data-vaul-no-drag
                role="list"
                className="divide-y divide-border px-4"
              >
                {cart
                  .sort((a, b) => {
                    const aProduct = flattenedMenus.find(
                      (product) => product.id === a.productId
                    );
                    const bProduct = flattenedMenus.find(
                      (product) => product.id === b.productId
                    );

                    if (aProduct === undefined || bProduct === undefined)
                      return 0;

                    const aName = aProduct.name + "-" + a.name;
                    const bName = bProduct.name + "-" + b.name;

                    if (aName > bName) return 1;
                    if (aName < bName) return -1;
                    return 0;
                  })
                  .map((product) => {
                    const parentProduct = flattenedMenus.find((item) =>
                      item.options?.some((variant) => variant.id === product.id)
                    );

                    const addons = product?.addons ?? [];
                    const condiments = product?.cartCondiment ?? [];

                    const linePrice =
                      product.price * product.count +
                      addons.reduce(
                        (total, addon) =>
                          total + addon.price * addon.count * product.count,
                        0
                      ) +
                      condiments.reduce(
                        (total, condiment) =>
                          total +
                          condiment.price * condiment.count * product.count,
                        0
                      );
                    const itemContribution =
                      rebate && parentProduct?.categoryId === rebate.categoryId
                        ? product.count * product.quantity
                        : 0;
                    const ratePerUnit =
                      rebate && rebate.discountPerSet % rebate.setSize === 0
                        ? rebate.discountPerSet / rebate.setSize
                        : 0;
                    const itemDiscount =
                      hasRebate &&
                      qualifyingCount % rebate!.setSize === 0 &&
                      itemContribution > 0
                        ? itemContribution * ratePerUnit
                        : 0;

                    return (
                      <li
                        key={product.lineId}
                        className="flex py-4 items-center gap-x-4"
                      >
                        <div className="flex flex-1 flex-col justify-between font-sans">
                          <button
                            className="relative block text-left"
                            type="button"
                            onClick={() => {
                              handleEditCartItem(product);
                            }}
                          >
                            <div className="flex w-full gap-2">
                              <div className="hidden relative h-12 w-12 max-w-12 object-cover object-center rounded-lg aspect-square overflow-hidden shrink-0 grow">
                                {parentProduct?.image &&
                                parentProduct?.image !==
                                  "/images/irondice.jpeg" ? (
                                  <img
                                    src={getImageUrl(
                                      menuId,
                                      parentProduct?.image ?? ""
                                    )}
                                    alt={""}
                                    className="h-full w-full object-cover object-center rounded-lg"
                                  />
                                ) : (
                                  <div className="flex border rounded-lg text-muted-foreground items-center justify-center h-full w-full">
                                    <ImageMinus />
                                  </div>
                                )}
                              </div>
                              <div className="flex justify-between w-full">
                                <div className="flex flex-col text-sm w-full">
                                  <span className="font-medium tracking-tight text-base text-foreground">
                                    {parentProduct && (
                                      <h3>{parentProduct.name}</h3>
                                    )}
                                  </span>
                                  <div className="flex justify-between w-full">
                                    <span
                                      className="text-muted-foreground text-sm"
                                      style={{ fontVariant: "no-contextual" }}
                                    >
                                      {product.count > 1 &&
                                        `${product.count}x `}
                                      {product.name}
                                    </span>
                                    {itemDiscount > 0 ? (
                                      <div className="flex flex-col items-end">
                                        <span className="line-through text-xs text-muted-foreground">
                                          {formatPriceToDkk(linePrice)}
                                        </span>
                                        <span className="font-medium text-foreground text-sm">
                                          {formatPriceToDkk(linePrice - itemDiscount)}
                                        </span>
                                      </div>
                                    ) : (
                                      <span
                                        className={cn(
                                          addons.length > 0 ||
                                            condiments.length > 0
                                            ? "text-muted-foreground text-xs"
                                            : "font-medium text-foreground text-sm"
                                        )}
                                      >
                                        {formatPriceToDkk(linePrice)}
                                      </span>
                                    )}
                                  </div>

                                  {addons.length > 0 && (
                                    <div className="flex flex-col gap-y-0 pt-1 w-full">
                                      {addons.map((addon) => {
                                        const addonCount =
                                          addon.count * product.count;
                                        return (
                                          <div
                                            key={addon.id}
                                            className="text-xs text-muted-foreground flex justify-between"
                                          >
                                            <p className="pr-1">{`+ ${addonCount}x ${addon.name}`}</p>
                                            <span className="whitespace-normal">
                                              {addon.price === 0
                                                ? formatPriceToDkk(0)
                                                : formatPriceToDkk(
                                                    addon.price * addonCount
                                                  )}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {condiments.length > 0 && (
                                    <div className="flex flex-col gap-y-0 pt-1 w-full">
                                      {condiments.map((condiment) => {
                                        const condimentCount =
                                          condiment.count * product.count;
                                        return (
                                          <div
                                            key={condiment.id}
                                            className="text-xs text-muted-foreground flex justify-between"
                                          >
                                            <p className="pr-1">{`+ ${condimentCount}x ${condiment.name}`}</p>
                                            <span className="whitespace-normal">
                                              {condiment.price === 0
                                                ? formatPriceToDkk(0)
                                                : formatPriceToDkk(
                                                    condiment.price *
                                                      condimentCount
                                                  )}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>

                          <div className="flex flex-col justify-center items-end gap-y-2 pt-2">
                            <QuantityInput
                              checkout
                              value={product.count}
                              onChange={(value) => {
                                setCount(product.lineId, value);
                                try {
                                  tracking.capture(
                                    "product_count_update",
                                    undefined,
                                    { product_id: product.id, count: value }
                                  );
                                } catch (error) {
                                  console.error(
                                    "Tracking event failed:",
                                    error
                                  );
                                }
                              }}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>

            {/* Fixed Footer with Terms and Removed Items */}
            <footer className="flex-shrink-0 border-t bg-background">
              {/* Removed Items Section */}
              {removedItems.length > 0 && (
                <section className="px-4 py-2 bg-yellow-50 border-b border-yellow-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0" />
                      <p className="text-xs font-medium text-yellow-800 leading-tight">
                        {removedItems.length === 1
                          ? "1 item no longer available"
                          : `${removedItems.length} items no longer available`}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearRemovedItems}
                      className="text-xs h-6 px-2 text-yellow-700 hover:bg-yellow-100 flex-shrink-0"
                    >
                      Dismiss
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-[80px] overflow-y-auto scrollbar-thin scrollbar-thumb-yellow-300 scrollbar-track-transparent">
                    {removedItems.map((item) => (
                      <div
                        key={item.id}
                        className="bg-yellow-100/50 rounded px-2 py-1 text-xs border border-yellow-200/50"
                      >
                        <span className="text-yellow-700 truncate block font-medium">
                          {item.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Terms of Service */}
              <div className="p-4">
                <a
                  href={`https://assets.table.taxi/global/terms.pdf`}
                  target="_blank"
                  className="underline text-sm text-muted-foreground"
                >
                  Terms of Service
                </a>
              </div>
            </footer>

            {/* Fixed Checkout Button */}
            <div
              className="sticky inset-x-0 bottom-0 z-[2] w-full px-4 pb-4 bg-background flex-shrink-0"
              data-vaul-no-drag
            >
              {hasRebate && (
                <div className="flex justify-between text-sm pb-2">
                  <span className="text-muted-foreground">
                    Rabat ({setsCount} × {rebate!.discountPerSet} kr)
                  </span>
                  <span>-{formatPriceToDkk(discountAmount)}</span>
                </div>
              )}
              <div className="px-0 flex flex-row gap-x-6 relative">
                <button
                  disabled={
                    cart.length === 0 || cart.every((item) => item.count === 0)
                  }
                  type="button"
                  onClick={() => {
                    if (hasUpsale) {
                      setShowUpsales ? setShowUpsales(true) : goToCheckout();
                    } else {
                      goToCheckout();
                    }
                  }}
                  data-vaul-no-drag
                  className="bg-primary flex flex-row rounded-md w-full h-auto items-center justify-between py-4 cursor-pointer text-primary-foreground px-4"
                >
                  <div className="flex flex-row gap-x-2 items-center">
                    <div className="bg-background size-6 flex items-center justify-center rounded-full aspect-square text-xs text-primary">
                      {cart.reduce((acc, item) => acc + item.count, 0)}
                    </div>
                    <span className="text-primary-foreground text-sm">
                      {isChequeOpen ? "Add to order" : "Go to checkout"}
                    </span>
                  </div>
                  <span className="text-sm">{formatPriceToDkk(finalPrice)}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
